import { compositeCombinedBlur } from './combinedBlurCapture';
import { toCanvas } from 'html-to-image';
import { getLinearLightFontEmbedCSS } from './fonts';
import type { Options } from 'html-to-image/lib/types';
import {
  compositeLinearLightPixels,
  getPaintedPixelBounds,
  loadLinearLightImage,
  waitForLinearLight,
} from './source';

// Chromium's SVG-as-image capture drops SVG backdrop filters. Flatten only the
// Linear Light layers in the task-owned preview tree, retaining native capture
// for every existing blend mode. No live editor tree is mutated here.
export async function flattenLinearLightForCapture(
  node: HTMLElement,
  options: Options = {}
) {
  await waitForLinearLight(node);
  const layers = Array.from(
    node.querySelectorAll<HTMLElement>('[data-linear-light-layer]')
  );
  if (!layers.length) return;
  const fontEmbedCSS =
    options.fontEmbedCSS ?? (await getLinearLightFontEmbedCSS(node));
  for (const layer of layers) {
    // Give the save progress UI a paint opportunity between costly captures.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    const effect = layer.querySelector<HTMLElement>(
      '[data-linear-light-effect]'
    );
    const image = layer
      .querySelector(':scope > svg')
      ?.querySelector<SVGImageElement>(
        'feImage[result="source"],feImage[result="captured-source"]'
      );
    const uri = image?.getAttribute('href');
    if (!effect || !uri || !layer.parentElement)
      throw new Error('Linear Light source is unavailable.');
    const following: HTMLElement[] = [];
    let branch: Element = layer;
    while (branch !== node && branch.parentElement) {
      const siblings = Array.from(branch.parentElement.children);
      following.push(
        ...siblings
          .slice(siblings.indexOf(branch) + 1)
          .filter(
            (element): element is HTMLElement => element instanceof HTMLElement
          )
      );
      branch = branch.parentElement;
    }
    const visibility = following.map((element) => element.style.visibility);
    const explicit = layer.querySelector<SVGSVGElement>(
      '[data-linear-light-explicit]'
    );
    const originalVisibility = explicit?.style.visibility || '';
    if (explicit) {
      effect.style.transform = explicit.style.transform;
      explicit.style.visibility = 'hidden';
    }
    const originalFilter = effect.style.getPropertyValue('backdrop-filter');
    const native = layer.querySelector<HTMLElement>(
      '[data-linear-light-source]'
    );
    const originalOpacity = native?.style.opacity || '';
    if (native) native.style.opacity = '0';
    const originalWebkitFilter = effect.style.getPropertyValue(
      '-webkit-backdrop-filter'
    );
    following.forEach((element) => {
      element.style.visibility = 'hidden';
    });
    effect.style.setProperty('backdrop-filter', 'none');
    effect.style.setProperty('-webkit-backdrop-filter', 'none');
    try {
      const backdrop = await toCanvas(node, { ...options, fontEmbedCSS });
      const context = backdrop.getContext('2d');
      if (!context)
        throw new Error('Linear Light capture requires a canvas context.');
      const rootBounds = node.getBoundingClientRect();
      const bounds = (explicit || effect).getBoundingClientRect();
      const scaleX = backdrop.width / rootBounds.width;
      const scaleY = backdrop.height / rootBounds.height;
      // The interactive backdrop is bounded, but its authored source PNG keeps
      // full-page coordinates. Read its explicit local placement, not the box.
      const paintOffset = layer.querySelector('feOffset[in="prepared-paint"]');
      const sourceOffset = layer.querySelector(
        'feOffset[in="captured-source"]'
      );
      const shiftedPaint = paintOffset || sourceOffset;
      const paintBounds =
        layer.hasAttribute('data-combined-prepared') ||
        sourceOffset ||
        (image.hasAttribute('width') &&
          !image.getAttribute('width')?.endsWith('%'))
          ? {
              left:
                bounds.left +
                Number(image.getAttribute('x')) +
                Number(shiftedPaint?.getAttribute('dx') || 0),
              top:
                bounds.top +
                Number(image.getAttribute('y')) +
                Number(shiftedPaint?.getAttribute('dy') || 0),
              width: Number(image.getAttribute('width')),
              height: Number(image.getAttribute('height')),
            }
          : bounds;
      const sourceImage = await loadLinearLightImage(uri);
      const maskUri = layer
        .querySelector(
          'feImage[result="footprint"],feImage[result="captured-footprint"]'
        )
        ?.getAttribute('href');
      if (layer.hasAttribute('data-combined-blur')) {
        if (!maskUri) throw new Error('Blur capture has no effect footprint.');
        await compositeCombinedBlur(
          backdrop,
          sourceImage,
          maskUri,
          {
            x: (paintBounds.left - rootBounds.left) * scaleX,
            y: (paintBounds.top - rootBounds.top) * scaleY,
            width: paintBounds.width * scaleX,
            height: paintBounds.height * scaleY,
          },
          Number(layer.dataset.combinedBlur) * scaleX,
          layer.dataset.combinedBlend || 'normal',
          Number(layer.dataset.linearLightOpacity),
          Number(layer.dataset.combinedFeather || 0) * scaleX,
          layer
            .querySelector('feImage[result*="paint-footprint"]')
            ?.getAttribute('href') || undefined
        );
      } else {
        const source = document.createElement('canvas');
        source.width = backdrop.width;
        source.height = backdrop.height;
        const sourceContext = source.getContext('2d');
        if (!sourceContext)
          throw new Error('Linear Light capture requires a canvas context.');
        sourceContext.drawImage(
          sourceImage,
          (paintBounds.left - rootBounds.left) * scaleX,
          (paintBounds.top - rootBounds.top) * scaleY,
          paintBounds.width * scaleX,
          paintBounds.height * scaleY
        );
        const sourcePixels = sourceContext.getImageData(
          0,
          0,
          source.width,
          source.height
        );
        const paint = getPaintedPixelBounds(
          sourcePixels.data,
          source.width,
          source.height
        );
        const isFullPage =
          paint.width === backdrop.width && paint.height === backdrop.height;
        const backgroundRegion = context.getImageData(
          paint.x,
          paint.y,
          paint.width,
          paint.height
        );
        const sourceRegion = isFullPage
          ? sourcePixels
          : sourceContext.getImageData(
              paint.x,
              paint.y,
              paint.width,
              paint.height
            );
        compositeLinearLightPixels(
          backgroundRegion.data,
          sourceRegion.data,
          Number(layer.dataset.linearLightOpacity)
        );
        context.putImageData(backgroundRegion, paint.x, paint.y);
      }
      // Keep the full overlay: cropping can change responsive backdrop sizing
      // and the image resampling performed by high-density thumbnail capture.
      const flattened = document.createElement('img');
      flattened.src = backdrop.toDataURL();
      flattened.alt = '';
      flattened.setAttribute('aria-hidden', 'true');
      Object.assign(flattened.style, {
        position: 'absolute',
        left: `${rootBounds.left - bounds.left}px`,
        top: `${rootBounds.top - bounds.top}px`,
        width: `${rootBounds.width}px`,
        height: `${rootBounds.height}px`,
        maxWidth: 'none',
      });
      await loadLinearLightImage(flattened.src);
      effect.append(flattened);
    } catch (error) {
      if (native) native.style.opacity = originalOpacity;
      if (explicit) explicit.style.visibility = originalVisibility;
      effect.style.setProperty('backdrop-filter', originalFilter);
      effect.style.setProperty('-webkit-backdrop-filter', originalWebkitFilter);
      throw error;
    } finally {
      following.forEach((element, index) => {
        element.style.visibility = visibility[index];
      });
    }
  }
}
